import { ArtTile, CountUp, formatInt } from '../index.js';

import type { StyleWithVars } from '../index.js';
import type { PartyWrapped } from '@mixwire/contracts';
import type { ReactElement } from 'react';

type Slide03TracksProps = {
  readonly wrapped: PartyWrapped;
};

const SPREAD_PERCENT = 88;
const MIN_WIDTH_CQW = 10;
const WIDTH_RANGE_CQW = 12;
const MIN_DURATION_S = 7;
const DURATION_RANGE_S = 6;
const MAX_HEAD_START_S = 8;
const TILT_RANGE_DEG = 40;
const GOLDEN_FRACTION = 0.618034;
const LANE_JITTER = 0.09;

const SUMMARY_STYLE: StyleWithVars = { '--d': '900' };

const NOISE_A = 12.9898;
const NOISE_B = 78.233;
const NOISE_SCALE = 43_758.5453;
const VALUES_PER_TILE = 5;

/**
 * A hash rather than `Math.random`: the scatter has to survive a re-render without the tiles
 * jumping to new lanes, and a seeded value gets that for free with no memo to keep in step.
 */
const unit = (seed: number): number => {
  const raw = Math.sin(seed * NOISE_A + NOISE_B) * NOISE_SCALE;

  return raw - Math.floor(raw);
};

/** Golden-ratio spacing, nudged: a plain hash clumps the lanes and leaves bald stripes. */
const laneAt = (index: number, jitter: number): number =>
  ((index * GOLDEN_FRACTION + jitter * LANE_JITTER) % 1) * SPREAD_PERCENT;

const driftStyle = (index: number): StyleWithVars => {
  const at = index * VALUES_PER_TILE;

  return {
    '--x': `${String(laneAt(index, unit(at)))}%`,
    '--w': `${String(MIN_WIDTH_CQW + unit(at + 1) * WIDTH_RANGE_CQW)}cqw`,
    '--dur': `${String(MIN_DURATION_S + unit(at + 2) * DURATION_RANGE_S)}s`,
    '--delay': `-${String(unit(at + 3) * MAX_HEAD_START_S)}s`,
    '--r': `${String((unit(at + 4) - 0.5) * TILT_RANGE_DEG)}deg`,
  };
};

export const Slide03Tracks = ({ wrapped }: Slide03TracksProps): ReactElement => {
  const { driftTracks, trackCount, uniqueTracks, uniqueArtists } = wrapped;

  return (
    <>
      <div className="drift-layer" aria-hidden="true">
        {driftTracks.map((track, index) => (
          <span
            className="drift drift-tile"
            key={`${String(index)}-${track.name}`}
            style={driftStyle(index)}
          >
            <ArtTile album={track.album} artUrl={track.artUrl} label={track.name} />
          </span>
        ))}
      </div>
      <p className="lede rise">The decks got through</p>
      <p className="mega stretch">
        <CountUp to={trackCount} format={formatInt} />
      </p>
      <p className="lede rise" style={SUMMARY_STYLE}>
        tracks:{' '}
        <b>
          <CountUp to={uniqueTracks} format={formatInt} delayMs={900} />
        </b>{' '}
        different songs by{' '}
        <b>
          <CountUp to={uniqueArtists} format={formatInt} delayMs={900} />
        </b>{' '}
        artists.
      </p>
    </>
  );
};
